import React, { useState } from 'react';
import { useStore } from '../store/StoreContext';
import { useToast } from '../hooks/useToast';
import { format, parseISO } from 'date-fns';
import { Modal, NumberField, Segmented, Button, StatusPill, inputCls, PageHeader, Toolbar, SearchInput, EmptyState, pageWrapCls, tableWrapCls, tableCls, thCls, thNumCls, trClickCls, trOpenCls, tdCls, tdNumCls, tdMutedCls, trExpandCls, tdExpandCls, kvCardCls, kvHeadCls, kvKeyCls, kvValCls } from '../components/ui';
import { IconArchive } from '../components/icons';
import { formatTime12 } from '../utils/formatters';
import { supabase } from '../lib/supabase';
import { estimateGroupBilling, phaseSeq, parseCharges } from '../lib/billing';

// Mint a signed invoice URL via the server endpoint. Server side owns
// DISPATCH_LINK_SECRET; this fetch passes the office user's Supabase JWT
// for auth. Replaces the previous browser-side HMAC generation (which had
// the secret bundled into the frontend — see 2026-05-14 security audit
// handoff in BBM Context for claude/security-backlog.md).
async function mintInvoiceUrl(jobId) {
    const { data: { session } } = await supabase.auth.getSession();
    const jwt = session?.access_token;
    if (!jwt) throw new Error('Not signed in');
    const resp = await fetch('/api/dispatch-token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ jobId, kind: 'invoice' }),
    });
    if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Invoice token mint failed (${resp.status}): ${text}`);
    }
    const { token, expiresAt } = await resp.json();
    const base = window.location.origin;
    return `${base}/api/invoice?job=${encodeURIComponent(jobId)}&exp=${expiresAt}&token=${token}`;
}

// Additional Charge Modal
function AddChargeModal({ job, onClose, onSave }) {
    const [desc, setDesc] = useState('');
    const [amount, setAmount] = useState('');
    const [saving, setSaving] = useState(false);
    const { show } = useToast();

    const handleSave = async () => {
        if (!desc || !amount) return;
        setSaving(true);
        try {
            await onSave(job, { description: desc, amount: parseFloat(amount) || 0, added_at: new Date().toISOString() });
        } catch (e) { show('Error: ' + (e.message || 'Unknown error'), 'error'); }
        finally { setSaving(false); }
    };

    return (
        <Modal title="Add Additional Charge" onClose={onClose} width="max-w-sm">
            <div className="space-y-3">
                <div>
                    <label className="text-xs font-bold text-slate-600">Description</label>
                    <input className={inputCls} value={desc} onChange={e => setDesc(e.target.value)} placeholder="Extra packing materials, long carry, etc." />
                </div>
                <div>
                    <label className="text-xs font-bold text-slate-600">Amount ($)</label>
                    <NumberField step={5} min={0} value={amount} onChange={v => setAmount(v)} placeholder="0.00" ariaLabel="Amount" />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} loading={saving} disabled={!desc || !amount}>Add Charge</Button>
                </div>
            </div>
        </Modal>
    );
}

function RecordsPage() {
    const { clients, crew, trucks, jobs, estimates, saveJob } = useStore();
    const [search, setSearch] = useState('');
    const [dateRange, setDateRange] = useState('all');
    const [expandedJob, setExpandedJob] = useState(null);
    const [chargeModal, setChargeModal] = useState(null);
    const [invoiceUrl, setInvoiceUrl] = useState(null);
    const { show: showToast } = useToast();

    // Stable sort: same-date rows tiebreak by phase then id so the order
    // can't shuffle between visits (QA M6 OBS-2).
    const completedJobs = jobs.filter(j => j.status === 'completed' && j.type !== 'WALKTHROUGH').sort((a, b) =>
        (b.move_date || '').localeCompare(a.move_date || '') ||
        (phaseSeq(a) - phaseSeq(b)) ||
        (a.id || '').localeCompare(b.id || ''));

    const filteredJobs = completedJobs.filter(j => {
        if (search) {
            const client = clients.find(c => c.id === j.client_id);
            const q = search.toLowerCase();
            const matches = (client?.name || '').toLowerCase().includes(q) || (j.from_address || '').toLowerCase().includes(q) || (j.to_address || '').toLowerCase().includes(q) || (j.invoice_number || '').toLowerCase().includes(q) || (j.job_label || '').toLowerCase().includes(q);
            if (!matches) return false;
        }
        if (dateRange !== 'all' && j.move_date) {
            const jDate = parseISO(j.move_date);
            const now = new Date();
            if (dateRange === 'month' && (now - jDate) > 30 * 86400000) return false;
            if (dateRange === 'quarter' && (now - jDate) > 90 * 86400000) return false;
            if (dateRange === 'year' && (now - jDate) > 365 * 86400000) return false;
        }
        return true;
    });

    // Group rows by estimate so multi-phase moves count their money ONCE
    // (QA M6 BUG-4 — two completed phases of a $1,000 estimate used to
    // show $2,000 quoted). Rows stay one-per-job; the estimate's money
    // lands on the PRIMARY leg (lowest phase_sequence, then earliest
    // date) and secondary legs point at it. Jobs without an estimate are
    // their own group of one — identical math to the old per-job path.
    const groupOf = new Map(); // job.id → { legs, primaryId, billing, estimate }
    {
        const byKey = new Map();
        for (const j of filteredJobs) {
            const key = j.estimate_id || `solo:${j.id}`;
            if (!byKey.has(key)) byKey.set(key, []);
            byKey.get(key).push(j);
        }
        for (const legs of byKey.values()) {
            const est = estimates.find(e => e.id === legs[0].estimate_id);
            const sorted = [...legs].sort((a, b) =>
                (phaseSeq(a) - phaseSeq(b)) ||
                (a.move_date || '').localeCompare(b.move_date || '') ||
                (a.id || '').localeCompare(b.id || ''));
            const entry = { legs: sorted, primaryId: sorted[0].id, billing: estimateGroupBilling(sorted, est), estimate: est };
            for (const l of sorted) groupOf.set(l.id, entry);
        }
    }
    const uniqueGroups = [...new Set(groupOf.values())];

    // Quoted vs actual rollup — summed per GROUP, so each estimate's
    // quote and variance count exactly once regardless of leg count.
    const { totalRevenue, totalQuoted } = uniqueGroups.reduce((acc, g) => {
        acc.totalRevenue += g.billing.displayTotal;
        acc.totalQuoted += g.billing.quotedTotal;
        return acc;
    }, { totalRevenue: 0, totalQuoted: 0 });
    const revenueDelta = totalRevenue - totalQuoted;

    const avgRating = (() => {
        const rated = filteredJobs.filter(j => j.customer_rating);
        return rated.length ? (rated.reduce((s, j) => s + j.customer_rating, 0) / rated.length).toFixed(1) : 'N/A';
    })();

    const totalHours = filteredJobs.reduce((s, j) => s + (Number(j.actual_hours) || Number(j.duration) || 0), 0);
    // Quoted hours also count once per estimate (same double-count shape).
    const totalQuotedHours = uniqueGroups.reduce((s, g) => s + g.billing.quotedHours, 0);

    const handleGenerateInvoice = async (job) => {
        try {
            const url = await mintInvoiceUrl(job.id);
            if (!job.invoice_number) {
                const num = 'INV-' + (job.move_date || '').replace(/-/g, '').slice(2) + '-' + job.id.slice(0, 4).toUpperCase();
                await saveJob({ ...job, invoice_number: num, invoice_generated_at: new Date().toISOString() });
                showToast('Invoice generated');
            }
            window.open(url, '_blank');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    const handleMarkPaid = async (job) => {
        try {
            // Settle the invoice. Payment method + amount are the canonical
            // move_jobs.payment_type / payment_amount, recorded by the crew
            // recap (or JobModal). We preserve whatever's recorded and don't
            // invent a method here — the old code hardcoded 'card' on the
            // legacy payment_method column, which was actively wrong.
            await saveJob({ ...job, invoice_paid: true, invoice_paid_at: new Date().toISOString() });
            showToast('Marked as paid');
        } catch (e) { showToast('Error: ' + e.message, 'error'); }
    };

    const unpaidCount = filteredJobs.filter(j => !j.invoice_paid).length;
    const summary = `${filteredJobs.length} ${filteredJobs.length === 1 ? 'job' : 'jobs'} · $${totalRevenue.toLocaleString('en-US', { maximumFractionDigits: 0 })} revenue · ${unpaidCount} unpaid`;

    return (
        <div className={pageWrapCls}>
            <PageHeader
                title="Records"
                summary={summary}
                help={<>
                    <p>Every completed move lands here once its crew recap is in. Expand a row to see the route, crew, recap notes and the balance still owed.</p>
                    <p className="mt-2">Use <strong>Invoice</strong> to generate a signed invoice link, <strong>Paid</strong> once the money is in, and <strong>+ Charge</strong> to add extras like packing materials or a long carry. Multi-phase moves show their money once, on the phase-1 row.</p>
                </>}
            />

            <Toolbar>
                <SearchInput value={search} onChange={setSearch} placeholder="Search by client, address, invoice #..." />
                <Segmented
                    size="sm"
                    ariaLabel="Date range"
                    items={[
                        { value: 'all', label: 'All Time' },
                        { value: 'month', label: 'Last 30 Days' },
                        { value: 'quarter', label: 'Last 90 Days' },
                        { value: 'year', label: 'Last Year' },
                    ]}
                    value={dateRange}
                    onChange={v => setDateRange(v)}
                    className="ml-auto"
                />
            </Toolbar>

            {/* Stats bar */}
            <div className="grid grid-cols-4 gap-4">
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-xs text-slate-500 font-bold uppercase">Completed Jobs</div>
                    <div className="text-2xl font-bold text-slate-900 mt-1">{filteredJobs.length}</div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-xs text-slate-500 font-bold uppercase">Actual Revenue</div>
                    <div className="text-2xl font-bold text-emerald-600 mt-1">${totalRevenue.toFixed(0)}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                        quoted ${totalQuoted.toFixed(0)}
                        {Math.abs(revenueDelta) >= 1 && (
                            <span className={revenueDelta < 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                                {' · '}{revenueDelta < 0 ? '−' : '+'}${Math.abs(revenueDelta).toFixed(0)} {revenueDelta < 0 ? 'under' : 'over'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-xs text-slate-500 font-bold uppercase">Actual Hours</div>
                    <div className="text-2xl font-bold text-slate-900 mt-1">{totalHours.toFixed(1)}h</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                        quoted {totalQuotedHours.toFixed(1)}h
                        {Math.abs(totalHours - totalQuotedHours) >= 0.1 && (
                            <span className={totalHours < totalQuotedHours ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                                {' · '}{totalHours < totalQuotedHours ? '−' : '+'}{Math.abs(totalHours - totalQuotedHours).toFixed(1)}h {totalHours < totalQuotedHours ? 'under' : 'over'}
                            </span>
                        )}
                    </div>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <div className="text-xs text-slate-500 font-bold uppercase">Avg Rating</div>
                    <div className="text-2xl font-bold text-amber-500 mt-1">{avgRating} {avgRating !== 'N/A' ? '★' : ''}</div>
                </div>
            </div>

            {/* Job records table — ledger recipe, see BBM Context for claude/lists.md §3 */}
            {(() => {
                // Per-job derived facts shared by the desktop row and the phone card.
                const rowCtx = (job) => {
                    const client = clients.find(c => c.id === job.client_id);
                    const group = groupOf.get(job.id);
                    const est = group.estimate;
                    const b = group.billing;
                    const isPrimary = group.primaryId === job.id;
                    const isMulti = group.legs.length > 1;
                    // Estimate money lives on the primary leg only.
                    const revenue = isPrimary ? b.displayTotal : 0;
                    const legActual = Number(job.actual_hours) > 0 ? Number(job.actual_hours) : null;
                    const charges = parseCharges(job);
                    const isExpanded = expandedJob === job.id;
                    return { client, group, est, b, isPrimary, isMulti, revenue, legActual, charges, isExpanded };
                };

                const renderHours = ({ job, b, group, isPrimary, isMulti, legActual }) => (
                    isMulti && !isPrimary ? (
                        // Secondary phase: its own hours are a real per-leg fact,
                        // but the estimate compare lives on the primary row.
                        legActual != null ? (
                            <div className="leading-tight">
                                <span className="text-blue-600 font-medium">{legActual}h</span>
                                <div className="text-[11px] text-slate-400 font-sans">this phase</div>
                            </div>
                        ) : <span className="text-slate-400">—</span>
                    ) : b.hasActual ? (
                        <div className="leading-tight">
                            <span className="text-blue-600 font-medium">{b.actualHours}h</span>
                            <div className="text-[11px] text-slate-400 font-sans">
                                est {b.quotedHours}h{isMulti ? ` · ${group.legs.length} phases` : ''}
                                {b.hoursDelta !== 0 && b.hoursDelta !== null && (
                                    <span className={b.hoursDelta < 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                                        {' · '}{b.hoursDelta < 0 ? '−' : '+'}{Math.abs(b.hoursDelta)}h {b.hoursDelta < 0 ? 'under' : 'over'}
                                    </span>
                                )}
                            </div>
                        </div>
                    ) : (
                        <span className="text-slate-400">{b.quotedHours}h est</span>
                    )
                );

                const renderRevenue = ({ b, group, isPrimary, isMulti, revenue }) => (
                    isMulti && !isPrimary ? (
                        <span className="text-[11px] text-slate-400 font-sans">in phase-1 row</span>
                    ) : revenue > 0 ? (
                        <div className="leading-tight">
                            <span className="font-medium text-emerald-600">${revenue.toFixed(0)}</span>
                            {b.hasActual && b.dollarsDelta !== 0 && b.dollarsDelta !== null && (
                                <div className="text-[11px] text-slate-400 font-sans">
                                    est ${b.quotedTotal.toFixed(0)}
                                    <span className={b.dollarsDelta < 0 ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                                        {' · '}{b.dollarsDelta < 0 ? '−' : '+'}${Math.abs(b.dollarsDelta).toFixed(0)}
                                    </span>
                                </div>
                            )}
                            {isMulti && <div className="text-[11px] text-slate-400 font-sans">all {group.legs.length} phases</div>}
                        </div>
                    ) : <span className="text-slate-400">—</span>
                );

                // Invoice status → StatusPill (lists.md §1: no rounded-full pills).
                const renderInvoice = (job) => (
                    job.invoice_paid ? <StatusPill label="Paid" color="#059669" /> : job.invoice_number ? <StatusPill label="Sent" color="#d97706" /> : <span className="text-slate-300">—</span>
                );

                const renderActions = (job) => (
                    <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                        <Button variant="secondary" size="sm" onClick={() => handleGenerateInvoice(job)}>Invoice</Button>
                        {!job.invoice_paid && <Button variant="secondary" size="sm" onClick={() => handleMarkPaid(job)}>Paid</Button>}
                        <Button variant="secondary" size="sm" onClick={() => setChargeModal(job)}>+ Charge</Button>
                    </div>
                );

                // Expansion content (route / crew / recap / charges / balance) — shared by
                // the desktop expansion row and the phone card.
                const renderExpansion = ({ job, est, group, isPrimary, isMulti, revenue, charges }) => (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase mb-1">Route</div>
                            <div className="text-slate-700">{job.from_address || 'N/A'}</div>
                            <div className="text-slate-400 text-xs my-0.5">&darr;</div>
                            <div className="text-slate-700">{job.to_address || 'N/A'}</div>
                        </div>
                        <div>
                            <div className="text-xs font-bold text-slate-400 uppercase mb-1">Crew</div>
                            <div className="flex flex-wrap gap-1">
                                {(job.crew_ids || []).map(cid => {
                                    const c = crew.find(m => m.id === cid);
                                    return c ? <span key={cid} className="text-xs px-2 py-0.5 rounded-md text-white" style={{backgroundColor: c.display_color}}>{c.name}{cid === job.leader_id ? ' (L)' : ''}</span> : null;
                                })}
                            </div>
                            {est && <div className="mt-2 text-xs text-slate-500">Estimate: {est.job_number} &bull; Rate: ${est.hourly_rate}/hr</div>}
                        </div>
                        <div>
                            {job.recap_submitted_at && (
                                <div>
                                    <div className="text-xs font-bold text-emerald-500 uppercase mb-1">Crew Recap</div>
                                    {job.actual_start_time && <div className="text-xs">{formatTime12(job.actual_start_time)} — {formatTime12(job.actual_end_time)} ({job.actual_hours}h)</div>}
                                    {job.crew_recap_notes && <div className="text-xs text-slate-600 mt-0.5">{job.crew_recap_notes}</div>}
                                    {job.damage_notes && <div className="text-xs text-red-600 mt-0.5 font-medium">Damage: {job.damage_notes}</div>}
                                </div>
                            )}
                            {charges.length > 0 && (
                                <div className="mt-2">
                                    <div className="text-xs font-bold text-slate-400 uppercase mb-1">Additional Charges</div>
                                    {charges.map((c, i) => <div key={i} className="text-xs flex justify-between"><span>{c.description}</span><span className="font-medium">${(c.amount || 0).toFixed(2)}</span></div>)}
                                </div>
                            )}
                            {/* Canonical payment (payment_type/payment_amount) with
                                legacy payment_method/notes fallback for old rows. */}
                            {(job.payment_type || Number(job.payment_amount) > 0 || job.payment_method) && (
                                <div className="mt-2 text-xs">
                                    <strong>Payment:</strong> {job.payment_type || job.payment_method}
                                    {Number(job.payment_amount) > 0 ? ` — $${Number(job.payment_amount).toFixed(2)}` : ''}
                                    {job.payment_notes ? ` — ${job.payment_notes}` : ''}
                                </div>
                            )}
                            {/* Reconciliation: amount due vs deposit vs collected.
                                Estimate-level, so it lives on the PRIMARY leg: due is
                                the group total, deposit counts once, and collected
                                sums every leg's payment (QA M6 BUG-4 — deposit used
                                to be subtracted once per phase row). Secondary legs
                                just show their own collection as a pointer. */}
                            {(() => {
                                if (isMulti && !isPrimary) {
                                    const collected = Number(job.payment_amount || 0);
                                    if (collected <= 0) return null;
                                    return (
                                        <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2">
                                            <div className="flex justify-between"><span className="text-slate-500">Collected this phase</span><span className="font-medium tabular-nums text-emerald-700">${collected.toFixed(2)}</span></div>
                                            <div className="text-slate-400 mt-0.5">Settled in the phase-1 row's balance.</div>
                                        </div>
                                    );
                                }
                                const due = revenue;
                                const deposit = est?.deposit_paid_at ? Number(est.deposit_amount || 0) : 0;
                                const collected = group.legs.reduce((s, l) => s + Number(l.payment_amount || 0), 0);
                                if (due <= 0 && deposit <= 0 && collected <= 0) return null;
                                const balance = due - deposit - collected;
                                return (
                                    <div className="mt-2 text-xs bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-0.5">
                                        <div className="flex justify-between"><span className="text-slate-500">Amount due{isMulti ? ` (${group.legs.length} phases)` : ''}</span><span className="font-medium tabular-nums">${due.toFixed(2)}</span></div>
                                        {deposit > 0 && <div className="flex justify-between"><span className="text-slate-500">Deposit paid</span><span className="font-medium tabular-nums text-emerald-700">−${deposit.toFixed(2)}</span></div>}
                                        {collected > 0 && <div className="flex justify-between"><span className="text-slate-500">Collected{isMulti ? ' (all phases)' : ''}</span><span className="font-medium tabular-nums text-emerald-700">−${collected.toFixed(2)}</span></div>}
                                        <div className="flex justify-between border-t border-slate-200 pt-0.5"><span className="font-semibold text-slate-700">Balance</span><span className={`font-bold tabular-nums ${balance > 0.005 ? 'text-rose-700' : 'text-emerald-700'}`}>${balance.toFixed(2)}</span></div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                );

                const filtersOn = !!search || dateRange !== 'all';
                const empty = filteredJobs.length === 0 && (
                    filtersOn ? (
                        <EmptyState
                            icon={<IconArchive size={28} />}
                            title="No records match"
                            hint="No completed jobs match this search or date range."
                            action={<Button variant="tertiary" size="sm" onClick={() => { setSearch(''); setDateRange('all'); }}>Clear filters</Button>}
                        />
                    ) : (
                        <EmptyState
                            icon={<IconArchive size={28} />}
                            title="No completed jobs yet"
                            hint="A move shows up here once its crew recap is submitted and the job is marked completed."
                            action={<Button size="sm" onClick={() => { window.location.hash = 'moves'; }}>Go to Active Moves</Button>}
                        />
                    )
                );

                if (empty) return empty;

                return (
                    <>
                        {/* Desktop / tablet: the ledger table */}
                        <div className={`hidden md:block ${tableWrapCls}`}>
                            <table className={tableCls}>
                                <thead>
                                    <tr>
                                        <th className={thCls}>Date</th>
                                        <th className={thCls}>Client</th>
                                        <th className={thCls}>Service</th>
                                        <th className={thNumCls}>Hours</th>
                                        <th className={thNumCls}>Revenue</th>
                                        <th className={thCls}>Rating</th>
                                        <th className={thCls}>Invoice</th>
                                        <th className={thCls}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredJobs.map(job => {
                                        const ctx = { job, ...rowCtx(job) };
                                        const { client, isExpanded } = ctx;

                                        return (
                                            <React.Fragment key={job.id}>
                                                <tr className={`${trClickCls} ${isExpanded ? trOpenCls : ''}`} onClick={() => setExpandedJob(isExpanded ? null : job.id)}>
                                                    <td className={`${tdCls} whitespace-nowrap`}>{job.move_date ? format(parseISO(job.move_date), 'MMM d, yyyy') : '—'}</td>
                                                    <td className={`${tdCls} font-medium`}>{client?.name || 'Unknown'}</td>
                                                    <td className={tdMutedCls}>{job.job_label || job.type}</td>
                                                    <td className={tdNumCls}>{renderHours(ctx)}</td>
                                                    <td className={tdNumCls}>{renderRevenue(ctx)}</td>
                                                    <td className={tdCls}>{job.customer_rating ? <span className="text-amber-500">{'★'.repeat(job.customer_rating)}</span> : <span className="text-slate-300">—</span>}</td>
                                                    <td className={tdCls}>{renderInvoice(job)}</td>
                                                    <td className={tdCls}>{renderActions(job)}</td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr className={trExpandCls}>
                                                        <td colSpan="8" className={tdExpandCls}>{renderExpansion(ctx)}</td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Phone: one key/value card per job (lists.md §3 phone fallback) */}
                        <div className="md:hidden space-y-2">
                            {filteredJobs.map(job => {
                                const ctx = { job, ...rowCtx(job) };
                                const { client, isExpanded } = ctx;
                                return (
                                    <div key={job.id} className={`${kvCardCls} ${isExpanded ? 'border-red-500 ring-[3px] ring-red-500/15' : ''}`} onClick={() => setExpandedJob(isExpanded ? null : job.id)}>
                                        <div className={kvHeadCls}>
                                            <span className="font-bold text-slate-900 truncate flex-1">{client?.name || 'Unknown'}</span>
                                            {renderInvoice(job)}
                                        </div>
                                        <span className={kvKeyCls}>Date</span>
                                        <span className={kvValCls}>{job.move_date ? format(parseISO(job.move_date), 'MMM d, yyyy') : '—'}</span>
                                        <span className={kvKeyCls}>Service</span>
                                        <span className={`${kvValCls} font-sans`}>{job.job_label || job.type}</span>
                                        <span className={kvKeyCls}>Hours</span>
                                        <span className={kvValCls}>{renderHours(ctx)}</span>
                                        <span className={kvKeyCls}>Revenue</span>
                                        <span className={kvValCls}>{renderRevenue(ctx)}</span>
                                        {job.customer_rating && (
                                            <>
                                                <span className={kvKeyCls}>Rating</span>
                                                <span className={`${kvValCls} text-amber-500`}>{'★'.repeat(job.customer_rating)}</span>
                                            </>
                                        )}
                                        <div className="col-span-2 mt-1">{renderActions(job)}</div>
                                        {isExpanded && (
                                            <div className="col-span-2 mt-2 pt-3 border-t border-slate-100" onClick={e => e.stopPropagation()}>
                                                {renderExpansion(ctx)}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                );
            })()}

            {/* Additional Charge Modal */}
            {chargeModal && <AddChargeModal job={chargeModal} onClose={() => setChargeModal(null)} onSave={async (job, charge) => {
                // parseCharges handles BOTH jsonb shapes (array from REST/
                // realtime, legacy string scalar) — a bare JSON.parse here
                // used to throw on the array form and WIPE existing charges
                // (QA M6 OBS-1). Write the array itself so the jsonb column
                // stores a proper array from now on.
                const existing = parseCharges(job);
                const updated = [...existing, charge];
                const est = estimates.find(e => e.id === job.estimate_id);
                const baseTotal = est?.total_estimate || 0;
                const chargeTotal = updated.reduce((s, c) => s + (parseFloat(c.amount) || 0), 0);
                await saveJob({ ...job, additional_charges: updated, total_charged: baseTotal + chargeTotal });
                setChargeModal(null);
                showToast('Charge added');
            }} />}

        </div>
    );
}

export default RecordsPage;
